// frontend/public/src/components/pages/admin/AdminSettings.jsx
import { useAuth } from '../../utils/auth';
import { useState, useEffect } from 'react';
import { previewWhiteLabel, resetWhiteLabel } from '../../utils/useWhiteLabel';
import api, { settings as settingsApi, sites as sitesApi, cash as cashApi, totpApi, passkeysApi } from '../../utils/api';
import { Save, Eye, RotateCcw, ExternalLink, Palette, Lock, KeyRound, Trash2 } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';
import TotpEnrollForm from '../../components/TotpEnrollForm';
import TotpBackupCodes from '../../components/TotpBackupCodes';

const FOND_BILLS = [
  { k:'fond_bill_100', label:'100 €', val:100, color:'#22d3a0' },
  { k:'fond_bill_50',  label:'50 €',  val:50,  color:'#22d3a0' },
  { k:'fond_bill_20',  label:'20 €',  val:20,  color:'#a387fd' },
  { k:'fond_bill_10',  label:'10 €',  val:10,  color:'#a387fd' },
  { k:'fond_bill_5',   label:'5 €',   val:5,   color:'#a387fd' },
];
const FOND_COINS = [
  { k:'fond_coin_200', label:'2 €',    val:2    },
  { k:'fond_coin_100', label:'1 €',    val:1    },
  { k:'fond_coin_50',  label:'0,50 €', val:0.5  },
  { k:'fond_coin_20',  label:'0,20 €', val:0.2  },
  { k:'fond_coin_10',  label:'0,10 €', val:0.1  },
  { k:'fond_coin_5',   label:'0,05 €', val:0.05 },
];

const ALL_BILLETS_CFG = [
  { key:'b_500', label:'500 €' }, { key:'b_200', label:'200 €' },
  { key:'b_100', label:'100 €' }, { key:'b_50',  label:'50 €'  },
  { key:'b_20',  label:'20 €'  }, { key:'b_10',  label:'10 €'  },
  { key:'b_5',   label:'5 €'   },
];
const ALL_PIECES_CFG = [
  { key:'p_200', label:'2 €'    }, { key:'p_100', label:'1 €'    },
  { key:'p_050', label:'0,50 €' }, { key:'p_020', label:'0,20 €' },
  { key:'p_010', label:'0,10 €' }, { key:'p_005', label:'0,05 €' },
  { key:'p_002', label:'0,02 €' }, { key:'p_001', label:'0,01 €' },
];

function FondDenomRow({ item, qty, onChange }) {
  const sub = qty * item.val;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10,
      padding:'6px 10px', borderRadius:7, marginBottom:4, background:'var(--bg-elevated)' }}>
      <span style={{ fontWeight:700, minWidth:44, color:item.color||'var(--text-2)', fontSize:14 }}>{item.label}</span>
      <span style={{ color:'var(--text-3)', fontSize:12 }}>×</span>
      <input type="number" min={0} value={qty||''}
        onChange={e => onChange(parseInt(e.target.value)||0)}
        onFocus={e => e.target.select()}
        style={{ width:56, textAlign:'center', background:'var(--bg-base)',
          border:'1px solid var(--border)', borderRadius:6, padding:'4px',
          color:'var(--text-1)', fontSize:14, fontWeight:600, outline:'none', fontFamily:'monospace' }}/>
      <span style={{ marginLeft:'auto', fontSize:13, color: qty>0?'var(--teal)':'var(--text-3)', fontWeight:600 }}>
        {qty > 0 ? `${sub.toFixed(2)} €` : '—'}
      </span>
    </div>
  );
}

function FondBySite() {
  const [sites, setSites]   = useState([]);
  const [siteId, setSiteId] = useState('');
  const [fond, setFond]     = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    sitesApi.list().then(r => {
      const s = r.data.sites || [];
      setSites(s);
      if (s[0]) setSiteId(s[0].id);
    });
  }, []);

  useEffect(() => {
    if (!siteId) return;
    cashApi.getFond(siteId).then(r => setFond(r.data.fond || {})).catch(() => {});
  }, [siteId]);

  const upd = (k, v) => setFond(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await cashApi.setFond(siteId, { fond });
      toast.success('Fond de caisse sauvegardé');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const totalBills = FOND_BILLS.reduce((s,d) => s + (parseInt(fond[d.k])||0) * d.val, 0);
  const totalCoins = FOND_COINS.reduce((s,d) => s + (parseInt(fond[d.k])||0) * d.val, 0);

  return (
    <div className="card" style={{ marginBottom:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <div className="card-title">Fond de caisse</div>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving||!siteId}>
          <Save size={13}/> {saving?'…':'Sauvegarder'}
        </button>
      </div>
      <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
        Composition initiale du tiroir-caisse par site.
      </p>
      {sites.length > 1 && (
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
          {sites.map(s => (
            <button key={s.id} type="button" onClick={() => setSiteId(s.id)}
              style={{ padding:'5px 14px', borderRadius:8,
                border:`1px solid ${siteId===s.id?(s.color||'var(--accent)'):'var(--border)'}`,
                background: siteId===s.id?`${s.color||'var(--accent)'}22`:'var(--bg-elevated)',
                color: siteId===s.id?(s.color||'var(--accent-light)'):'var(--text-2)',
                cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: siteId===s.id?700:400 }}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>💵 Billets</div>
          {FOND_BILLS.map(d => <FondDenomRow key={d.k} item={d} qty={parseInt(fond[d.k])||0} onChange={v => upd(d.k, v)}/>)}
          <div style={{ textAlign:'right', fontWeight:700, color:'var(--teal)', padding:'6px 10px', borderTop:'1px solid var(--border)', marginTop:4, fontSize:13 }}>
            {totalBills.toFixed(2)} €
          </div>
        </div>
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>🪙 Pièces</div>
          {FOND_COINS.map(d => <FondDenomRow key={d.k} item={d} qty={parseInt(fond[d.k])||0} onChange={v => upd(d.k, v)}/>)}
          <div style={{ textAlign:'right', fontWeight:700, color:'var(--teal)', padding:'6px 10px', borderTop:'1px solid var(--border)', marginTop:4, fontSize:13 }}>
            {totalCoins.toFixed(2)} €
          </div>
        </div>
      </div>
      <div style={{ marginTop:12, padding:'10px 14px', background:'var(--bg-elevated)',
        borderRadius:8, display:'flex', justifyContent:'space-between', fontWeight:700 }}>
        <span>Total fond — {sites.find(s=>s.id===siteId)?.name}</span>
        <span style={{ color:'var(--teal)', fontSize:16 }}>{(totalBills+totalCoins).toFixed(2)} €</span>
      </div>
    </div>
  );
}

// ── Onglet Caisse ─────────────────────────────────────────────
function CaisseTab() {
  const [enabledBills,  setEnabledBills]  = useState(['b_100','b_50','b_20','b_10','b_5']);
  const [enabledCoins,  setEnabledCoins]  = useState(['p_200','p_100','p_050','p_020','p_010','p_005']);
  const [cashThreshold, setCashThreshold] = useState('');
  const [safeThreshold, setSafeThreshold] = useState('');
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [sites,         setSites]         = useState([]);
  const [safeInits,     setSafeInits]     = useState({}); // { siteId: { amount, date } }
  const [savingInit,     setSavingInit]    = useState(false);
  const [especesInCaisse, setEspecesInCaisse] = useState({});
  const [savingEspeces,   setSavingEspeces]  = useState(false);

  useEffect(() => {
    api.get('/sites').then(r => setSites(r.data.sites || [])).catch(() => {});
    api.get('/settings').then(r => {
      const s = {};
      (r.data.settings || []).forEach(x => s[x.key] = x.value);
      try { const eb = JSON.parse(s.enabled_bills || '[]'); if (eb.length) setEnabledBills(eb); } catch {}
      try { const ec = JSON.parse(s.enabled_coins || '[]'); if (ec.length) setEnabledCoins(ec); } catch {}
      setCashThreshold(s.cash_alert_threshold || '');
      setSafeThreshold(s.safe_alert_threshold || '');
      // Paramètre espèces dans caisse ou coffre par site
      const eic = {};
      Object.entries(s).forEach(([key, val]) => {
        const m = key.match(/^especes_in_caisse_(.+)$/);
        if (m) eic[m[1]] = val !== 'false';
      });
      setEspecesInCaisse(eic);

      // Soldes initiaux coffre par site
      const inits = {};
      Object.entries(s).forEach(([key, val]) => {
        const m = key.match(/^safe_initial_amount_(.+)$/);
        if (m) {
          const sid = m[1];
          if (!inits[sid]) inits[sid] = {};
          inits[sid].amount = val;
        }
        const m2 = key.match(/^safe_initial_date_(.+)$/);
        if (m2) {
          const sid = m2[1];
          if (!inits[sid]) inits[sid] = {};
          inits[sid].date = val;
        }
      });
      // Fallback global
      if (!Object.keys(inits).length) {
        inits['_global'] = { amount: s.safe_initial_amount || '0', date: s.safe_initial_date || '' };
      }
      setSafeInits(inits);
    }).catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key, list, setList) =>
    setList(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put('/settings/enabled_bills',        { value: JSON.stringify(enabledBills) }),
        api.put('/settings/enabled_coins',        { value: JSON.stringify(enabledCoins) }),
        api.put('/settings/cash_alert_threshold', { value: cashThreshold }),
        api.put('/settings/safe_alert_threshold', { value: safeThreshold }),
      ]);
      toast.success('Paramètres caisse enregistrés');
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="empty-state"><span className="spinner"/></div>;

  return (
    <div style={{ maxWidth:600 }}>
      <FondBySite/>

      {/* Mode comptage par site */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-title" style={{ marginBottom:4 }}>Mode comptage caisse par site</div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
          Définit si les espèces du jour sont dans la caisse (tiroir) ou dans le coffre directement.
        </p>
        {sites.map(site => {
          const val = especesInCaisse[site.id] !== false;
          return (
            <div key={site.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'10px 14px', borderRadius:10, marginBottom:8,
              background:'var(--bg-elevated)', border:`1px solid ${val?'var(--teal)':'var(--border)'}` }}>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color: site.color||'var(--accent-light)' }}>{site.name}</div>
                <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>
                  {val ? 'Espèces du jour dans la caisse (tiroir)' : 'Espèces du jour dans le coffre'}
                </div>
              </div>
              <div onClick={() => setEspecesInCaisse(p => ({...p, [site.id]: !val}))}
                style={{ width:44, height:24, borderRadius:12, cursor:'pointer',
                  background: val ? 'var(--teal)' : 'var(--bg-base)',
                  border:'1px solid var(--border)', position:'relative', transition:'background 0.2s' }}>
                <div style={{ position:'absolute', top:3, left: val ? 22 : 3,
                  width:16, height:16, borderRadius:'50%', background:'#fff',
                  transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
              </div>
            </div>
          );
        })}
        <button className="btn btn-secondary" style={{ marginTop:8 }}
          disabled={savingEspeces} onClick={async () => {
          setSavingEspeces(true);
          try {
            for (const site of sites) {
              const val = especesInCaisse[site.id] !== false;
              await api.put(`/settings/especes_in_caisse_${site.id}`, { value: String(val) });
            }
            toast.success('Paramètres enregistrés');
          } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
          finally { setSavingEspeces(false); }
        }}>
          {savingEspeces ? <span className="spinner" style={{width:14,height:14}}/> : <><Save size={14}/> Enregistrer</>}
        </button>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-title" style={{ marginBottom:4 }}>Coupures actives</div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
          Seules les coupures cochées apparaissent dans l'interface de comptage coffre.
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-3)', marginBottom:10, textTransform:'uppercase' }}>💵 Billets</div>
            {ALL_BILLETS_CFG.map(c => (
              <label key={c.key} style={{ display:'flex', alignItems:'center', gap:10,
                padding:'6px 10px', borderRadius:8, marginBottom:4, cursor:'pointer',
                background: enabledBills.includes(c.key) ? 'rgba(0,210,180,0.08)' : 'var(--bg-elevated)',
                border:`1px solid ${enabledBills.includes(c.key) ? 'rgba(0,210,180,0.3)' : 'var(--border)'}` }}>
                <input type="checkbox" checked={enabledBills.includes(c.key)}
                  onChange={() => toggle(c.key, enabledBills, setEnabledBills)}
                  style={{ accentColor:'var(--teal)' }}/>
                <span style={{ fontWeight:700, fontSize:13,
                  color: enabledBills.includes(c.key) ? 'var(--teal)' : 'var(--text-2)' }}>
                  {c.label}
                </span>
              </label>
            ))}
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--text-3)', marginBottom:10, textTransform:'uppercase' }}>🪙 Pièces</div>
            {ALL_PIECES_CFG.map(c => (
              <label key={c.key} style={{ display:'flex', alignItems:'center', gap:10,
                padding:'6px 10px', borderRadius:8, marginBottom:4, cursor:'pointer',
                background: enabledCoins.includes(c.key) ? 'rgba(0,210,180,0.08)' : 'var(--bg-elevated)',
                border:`1px solid ${enabledCoins.includes(c.key) ? 'rgba(0,210,180,0.3)' : 'var(--border)'}` }}>
                <input type="checkbox" checked={enabledCoins.includes(c.key)}
                  onChange={() => toggle(c.key, enabledCoins, setEnabledCoins)}
                  style={{ accentColor:'var(--teal)' }}/>
                <span style={{ fontWeight:700, fontSize:13,
                  color: enabledCoins.includes(c.key) ? 'var(--teal)' : 'var(--text-2)' }}>
                  {c.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-title" style={{ marginBottom:4 }}>Seuils d'alerte</div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
          Une confirmation est demandée si l'écart dépasse ce seuil. Laisser vide pour désactiver.
        </p>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Seuil alerte caisse (€)</label>
            <input className="input" type="text" inputMode="decimal"
              value={cashThreshold} onChange={e => setCashThreshold(e.target.value)}
              placeholder="ex: 10 (vide = désactivé)"/>
          </div>
          <div className="form-group">
            <label className="form-label">Seuil alerte coffre (€)</label>
            <input className="input" type="text" inputMode="decimal"
              value={safeThreshold} onChange={e => setSafeThreshold(e.target.value)}
              placeholder="ex: 20 (vide = désactivé)"/>
          </div>
        </div>
      </div>

      {/* Soldes initiaux coffre par site */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-title" style={{ marginBottom:4 }}>Solde initial coffre par site</div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
          Saisissez le solde de départ du coffre pour chaque site. À faire une seule fois.
        </p>
        {sites.map(site => {
          const init = safeInits[site.id] || {};
          return (
            <div key={site.id} style={{ marginBottom:16, padding:'12px 16px',
              background:'var(--bg-elevated)', borderRadius:10 }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:10,
                color: site.color || 'var(--accent-light)' }}>{site.name}</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Solde initial (€)</label>
                  <input className="input" type="text" inputMode="decimal"
                    value={init.amount || ''}
                    onChange={e => setSafeInits(p => ({...p, [site.id]: {...(p[site.id]||{}), amount: e.target.value}}))}
                    placeholder="ex: 1250,00"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Date de départ</label>
                  <input className="input" type="date"
                    value={(init.date||'').slice(0,10)}
                    onChange={e => setSafeInits(p => ({...p, [site.id]: {...(p[site.id]||{}), date: e.target.value}}))}/>
                </div>
              </div>
            </div>
          );
        })}
        <button className="btn btn-secondary" disabled={savingInit} onClick={async () => {
          setSavingInit(true);
          try {
            for (const site of sites) {
              const init = safeInits[site.id] || {};
              if (init.amount) {
                await api.post('/safe/init', {
                  amount: parseFloat(String(init.amount).replace(',','.')),
                  date: init.date || new Date().toISOString(),
                  siteId: site.id,
                });
              }
            }
            toast.success('Soldes initiaux enregistrés');
          } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
          finally { setSavingInit(false); }
        }}>
          {savingInit ? <span className="spinner" style={{width:14,height:14}}/> : <><Save size={14}/> Enregistrer les soldes initiaux</>}
        </button>
      </div>

      <button className="btn btn-primary" onClick={save} disabled={saving}>
        {saving ? <span className="spinner" style={{width:14,height:14}}/> : <><Save size={14}/> Enregistrer</>}
      </button>
    </div>
  );
}

// Petite modale pour nommer la clé après la cérémonie WebAuthn (remplace
// window.prompt — cohérent avec le style de l'app plutôt qu'une alerte navigateur).
function PasskeyNameModal({ onConfirm, onCancel, busy }) {
  const [name, setName] = useState('');
  return (
    // Classes standard modal-overlay/modal (plutôt que des styles ad hoc) :
    // reçoit gratuitement Entrée = valider / Échap = annuler via le
    // mécanisme générique <ModalHotkeys/>.
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !busy && onCancel()}>
      <div className="modal" style={{ maxWidth:360 }}>
        <div className="card-title" style={{ marginBottom:4 }}>Nommer cette clé d'accès</div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:14 }}>
          Pour la reconnaître plus tard (ex: iPhone, YubiKey).
        </p>
        <input className="input" autoFocus value={name} onChange={e => setName(e.target.value)}
          placeholder="ex: iPhone de Paul"
          onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); onConfirm(name); } }}
          style={{ marginBottom:16 }}/>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>Annuler</button>
          <button className="btn btn-primary btn-sm" onClick={() => onConfirm(name)} disabled={busy}>
            {busy ? <span className="spinner" style={{width:14,height:14}}/> : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Clés d'accès (passkeys) — carte dans l'onglet Sécurité ───────
function PasskeysCard() {
  const [passkeys, setPasskeys] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingResponse, setPendingResponse] = useState(null); // réponse WebAuthn en attente d'un nom
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = () => passkeysApi.list().then(r => setPasskeys(r.data.passkeys)).catch(() => {});
  useEffect(() => { load(); }, []);

  const register = async () => {
    setBusy(true);
    try {
      const { data: options } = await passkeysApi.registerOptions();
      const response = await startRegistration(options);
      setPendingResponse(response); // ouvre la modale de nommage
    } catch(e) {
      if (e?.name !== 'NotAllowedError') toast.error(e.response?.data?.error || 'Erreur');
    } finally { setBusy(false); }
  };

  const confirmName = async (name) => {
    setBusy(true);
    try {
      await passkeysApi.registerVerify({ response: pendingResponse, label: name || undefined });
      toast.success("Clé d'accès ajoutée");
      setPendingResponse(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await passkeysApi.remove(confirmRemove.id);
      load();
      setConfirmRemove(null);
    } catch { toast.error('Erreur'); }
    finally { setRemoving(false); }
  };

  if (!passkeys) return null;

  return (
    <div className="card" style={{ padding:20 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <KeyRound size={16} color={passkeys.length ? 'var(--teal)' : 'var(--text-3)'}/>
        <div className="card-title" style={{ margin:0 }}>Clés d'accès</div>
        <button className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }} onClick={register} disabled={busy}>
          + Ajouter
        </button>
      </div>
      <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:14 }}>
        Connectez-vous sans mot de passe avec Face ID, Touch ID, Windows Hello ou une clé de sécurité.
      </p>
      {passkeys.length === 0 ? (
        <p style={{ fontSize:13, color:'var(--text-3)' }}>Aucune clé d'accès enregistrée</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {passkeys.map(p => (
            <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13 }}>
              <span style={{ flex:1 }}>{p.name || 'Clé sans nom'}</span>
              <span style={{ color:'var(--text-3)', fontSize:12 }}>
                {new Date(p.created_at).toLocaleDateString('fr-FR')}
              </span>
              <button onClick={() => setConfirmRemove(p)} style={{ background:'none', border:'none',
                color:'var(--red)', cursor:'pointer', display:'flex' }}>
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
        </div>
      )}
      {pendingResponse && (
        <PasskeyNameModal busy={busy} onConfirm={confirmName} onCancel={() => setPendingResponse(null)}/>
      )}
      {confirmRemove && (
        <ConfirmModal
          title="Supprimer la clé d'accès"
          message={`Supprimer "${confirmRemove.name || 'Clé sans nom'}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

// ── Onglet Sécurité (2FA) ───────────────────────────────────────
function SecuriteTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [mode, setMode]     = useState('idle'); // idle | enroll | codes | disable
  const [enrollData, setEnrollData]   = useState(null);
  const [enrollError, setEnrollError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');

  const load = () => totpApi.status().then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await totpApi.setup();
      setEnrollData(r.data);
      setMode('enroll');
    } catch { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  const verifyEnroll = async (code) => {
    setEnrollError(null);
    setBusy(true);
    try {
      const r = await totpApi.enable({ secret: enrollData.secret, code });
      setBackupCodes(r.data.backupCodes || []);
      setMode('codes');
    } catch(e) { setEnrollError(e.response?.data?.error || 'Code invalide'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await totpApi.disable({ password: disablePassword });
      toast.success('2FA désactivée');
      setMode('idle'); setDisablePassword('');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const toggleOrgEnforced = async () => {
    setBusy(true);
    try {
      await totpApi.setOrgEnforced({ enforced: !status.org_enforced });
      toast.success(!status.org_enforced
        ? '2FA imposée à tous les comptes de l\'organisation'
        : '2FA non imposée sur l\'organisation');
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  if (!status) return <div style={{ textAlign:'center', padding:32 }}><span className="spinner"/></div>;

  if (mode === 'enroll') {
    return (
      <div className="card" style={{ maxWidth:480, margin:'0 auto', padding:24 }}>
        <div className="card-title" style={{ marginBottom:16 }}>Activer la double authentification</div>
        <TotpEnrollForm qrDataUrl={enrollData.qrDataUrl} secret={enrollData.secret}
          onVerify={verifyEnroll} loading={busy} error={enrollError}/>
      </div>
    );
  }
  if (mode === 'codes') {
    return (
      <div className="card" style={{ maxWidth:480, margin:'0 auto', padding:24 }}>
        <div className="card-title" style={{ marginBottom:16 }}>2FA activée</div>
        <TotpBackupCodes codes={backupCodes || []} onDone={() => { setMode('idle'); load(); }}/>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16, maxWidth:560 }}>
      <div className="card" style={{ padding:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <Lock size={16} color={status.totp_enabled ? 'var(--teal)' : 'var(--text-3)'}/>
          <div className="card-title" style={{ margin:0 }}>Mon compte</div>
          <span style={{ marginLeft:'auto', fontSize:12, fontWeight:600,
            color: status.totp_enabled ? 'var(--teal)' : 'var(--text-3)' }}>
            {status.totp_enabled ? 'Activée' : 'Désactivée'}
          </span>
        </div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:14 }}>
          Protégez votre compte avec un code à usage unique en plus de votre mot de passe.
        </p>
        {status.totp_enabled ? (
          mode === 'disable' ? (
            <div>
              <input type="password" className="input" placeholder="Mot de passe" value={disablePassword}
                onChange={e => setDisablePassword(e.target.value)} style={{ marginBottom:8 }}/>
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setMode('idle')}>Annuler</button>
                <button className="btn btn-sm" onClick={disable} disabled={busy || !disablePassword}
                  style={{ background:'var(--red)', color:'#fff' }}>
                  Désactiver
                </button>
              </div>
            </div>
          ) : (
            <button className="btn btn-secondary btn-sm" onClick={() => setMode('disable')}
              disabled={status.org_enforced} title={status.org_enforced ? 'Imposée par votre organisation' : ''}>
              Désactiver la 2FA
            </button>
          )
        ) : (
          <button className="btn btn-primary btn-sm" onClick={startEnroll} disabled={busy}>
            Activer la 2FA
          </button>
        )}
        {status.org_enforced && !status.totp_enabled && (
          <p style={{ fontSize:12, color:'var(--amber)', marginTop:8 }}>
            ⚠ Votre organisation impose la double authentification — configurez-la dès maintenant.
          </p>
        )}
      </div>

      <PasskeysCard/>

      <div className="card" style={{ padding:20 }}>
        <div className="card-title" style={{ marginBottom:6 }}>Toute l'organisation</div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:14 }}>
          Impose la double authentification à tous les comptes de l'équipe. Les comptes non
          encore configurés devront l'activer à leur prochaine connexion.
        </p>
        <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
          <input type="checkbox" checked={!!status.org_enforced} onChange={toggleOrgEnforced} disabled={busy}/>
          <span style={{ fontSize:13, fontWeight:600 }}>
            Imposer la 2FA à toute l'équipe
          </span>
        </label>
      </div>
    </div>
  );
}

// ── Onglet Marque Blanche ─────────────────────────────────────
const WL_DEFAULTS = {
  appName: 'EscapeOS', logoUrl: '', faviconUrl: '',
  primaryColor: '#7c5cfc', accentColor: '#00d2b4', bgColor: '#0d0d1a',
  customDomain: '', isActive: false,
};

function WhiteLabelTab() {
  const [form,   setForm]   = useState(WL_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [preview,setPreview]= useState(false);

  useEffect(() => {
    api.get('/white-label').then(r => {
      if (r.data.config) setForm({
        appName:      r.data.config.app_name      || WL_DEFAULTS.appName,
        logoUrl:      r.data.config.logo_url       || '',
        faviconUrl:   r.data.config.favicon_url    || '',
        primaryColor: r.data.config.primary_color  || WL_DEFAULTS.primaryColor,
        accentColor:  r.data.config.accent_color   || WL_DEFAULTS.accentColor,
        bgColor:      r.data.config.bg_color       || WL_DEFAULTS.bgColor,
        customDomain: r.data.config.custom_domain  || '',
        isActive:     r.data.config.is_active      || false,
      });
    }).catch(() => {});
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePreview = () => {
    if (preview) { resetWhiteLabel(); setPreview(false); }
    else         { previewWhiteLabel(form); setPreview(true); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/white-label', form);
      toast.success('Marque blanche sauvegardée');
      if (preview) previewWhiteLabel(form);
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const ColorRow = ({ label, field, hint }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <input type="color" value={form[field]} onChange={e => upd(field, e.target.value)}
          style={{ width:44, height:36, padding:2, borderRadius:8,
            border:'1px solid var(--border)', cursor:'pointer', background:'none' }}/>
        <input className="input" value={form[field]} onChange={e => upd(field, e.target.value)}
          style={{ flex:1, fontFamily:'monospace' }} maxLength={7}/>
        <div style={{ width:36, height:36, borderRadius:8, background:form[field],
          border:'1px solid var(--border)', flexShrink:0 }}/>
      </div>
      {hint && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>{hint}</div>}
    </div>
  );

  return (
    <div>
      {preview && (
        <div style={{ marginBottom:16, padding:'10px 16px', borderRadius:10,
          background:'rgba(245,166,35,0.08)', border:'1px solid var(--amber)',
          fontSize:13, color:'var(--amber)' }}>
          ⚠ Mode prévisualisation actif
        </div>
      )}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:16 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom:14 }}>Identité</div>
          <div className="form-group">
            <label className="form-label">Nom de l'application</label>
            <input className="input" value={form.appName} onChange={e => upd('appName', e.target.value)} placeholder="EscapeOS"/>
          </div>
          <div className="form-group">
            <label className="form-label">URL du logo</label>
            <input className="input" value={form.logoUrl} onChange={e => upd('logoUrl', e.target.value)} placeholder="https://…/logo.png"/>
            {form.logoUrl && (
              <div style={{ marginTop:8, padding:12, background:'var(--bg-elevated)', borderRadius:8, display:'flex', justifyContent:'center' }}>
                <img src={form.logoUrl} alt="Logo" style={{ maxHeight:60, maxWidth:'100%', objectFit:'contain' }}
                  onError={e => { e.target.style.display='none'; }}/>
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">URL du favicon</label>
            <input className="input" value={form.faviconUrl} onChange={e => upd('faviconUrl', e.target.value)} placeholder="https://…/favicon.ico"/>
          </div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom:14 }}>Couleurs</div>
          <ColorRow label="Couleur principale" field="primaryColor" hint="Boutons, accents"/>
          <ColorRow label="Couleur secondaire" field="accentColor" hint="Statuts positifs"/>
          <ColorRow label="Fond de base" field="bgColor" hint="Arrière-plan principal"/>
          <div style={{ marginTop:8, padding:12, borderRadius:10, background:form.bgColor, border:'1px solid var(--border)' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:6 }}>Aperçu</div>
            <div style={{ display:'flex', gap:8 }}>
              <span style={{ padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:700, background:form.primaryColor, color:'#fff' }}>Bouton</span>
              <span style={{ padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700, background:form.accentColor+'22', color:form.accentColor }}>Badge</span>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom:14 }}>Domaine personnalisé</div>
          <div className="form-group">
            <label className="form-label">Domaine</label>
            <input className="input" value={form.customDomain} onChange={e => upd('customDomain', e.target.value)} placeholder="app.monescapegame.fr"/>
            <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>
              Configurez un CNAME pointant vers votre VPS
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom:14 }}>Activation</div>
          <div style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 14px', borderRadius:10,
            background:'var(--bg-elevated)', border:`1px solid ${form.isActive?'var(--teal)':'var(--border)'}` }}>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:13 }}>{form.isActive ? '✓ Active' : 'Désactivée'}</div>
              <div style={{ fontSize:11, color:'var(--text-3)', marginTop:2 }}>
                {form.isActive ? 'Vos couleurs et logo sont appliqués' : 'Activez pour personnaliser'}
              </div>
            </div>
            <div onClick={() => upd('isActive', !form.isActive)}
              style={{ width:44, height:24, borderRadius:12, cursor:'pointer',
                background: form.isActive ? 'var(--teal)' : 'var(--bg-base)',
                border:'1px solid var(--border)', position:'relative', transition:'background 0.2s' }}>
              <div style={{ position:'absolute', top:3, left: form.isActive ? 22 : 3,
                width:16, height:16, borderRadius:'50%', background:'#fff',
                transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button className={`btn ${preview?'btn-primary':'btn-secondary'}`} onClick={handlePreview}>
          {preview ? <><RotateCcw size={13}/> Réinitialiser</> : <><Eye size={13}/> Prévisualiser</>}
        </button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <span className="spinner" style={{width:14,height:14}}/> : <><Save size={13}/> Sauvegarder</>}
        </button>
      </div>
    </div>
  );
}

export function AdminSettings() {
  const { can } = useAuth();
  if (!can('admin.settings')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [settingsList, setSettingsList] = useState([]);
  const [values,       setValues]       = useState({});
  const [loading,      setLoading]      = useState(false);
  const [activeTab,    setActiveTab]    = useState('general');

  useEffect(() => {
    settingsApi.get().then(r => {
      setSettingsList(r.data.settings);
      const v = {};
      r.data.settings.forEach(s => v[s.key] = s.value || '');
      setValues(v);
    });
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await settingsApi.update({ settings: Object.entries(values).map(([key, value]) => ({ key, value })) });
      toast.success('Paramètres sauvegardés');
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const upd = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const groups = {};
  settingsList.forEach(s => {
    const group = s.key.startsWith('smtp') || s.key.startsWith('sms') ? 'Notifications'
      : s.key.startsWith('business') ? 'Établissement' : 'Général';
    if (!groups[group]) groups[group] = [];
    groups[group].push(s);
  });

  const TABS = [
    { id:'general',       label:'Général' },
    { id:'etablissement', label:'Établissement' },
    { id:'notifications', label:'Notifications' },
    { id:'caisse',        label:'Caisse' },
    { id:'securite',      label:'Sécurité' },
    { id:'whitelabel',    label:'Marque blanche' },
  ];

  return (
    <>
      <div className="page-header">
        <div><h2>Paramètres</h2><p>Configuration générale du système</p></div>
        {activeTab !== 'whitelabel' && activeTab !== 'caisse' && activeTab !== 'securite' && (
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            <Save size={15}/> {loading ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        )}
      </div>
      <div className="page-body">
        {/* overflowX + flexShrink:0 sur chaque bouton : sur mobile la rangée de
            6 onglets ne tenait pas dans la largeur d'écran et les derniers
            (Sécurité, Marque blanche) étaient simplement coupés/inatteignables
            au lieu de défiler. */}
        <div style={{ display:'flex', gap:6, marginBottom:20, borderBottom:'1px solid var(--border)',
          paddingBottom:12, overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{
                padding:'7px 16px', borderRadius:8, border:'none', cursor:'pointer',
                fontFamily:'inherit', fontSize:13, fontWeight:600, whiteSpace:'nowrap', flexShrink:0,
                background: activeTab===t.id ? 'var(--accent)' : 'var(--bg-elevated)',
                color: activeTab===t.id ? '#fff' : 'var(--text-2)',
                transition:'all 0.12s',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'whitelabel' && <WhiteLabelTab/>}
        {activeTab === 'caisse'     && <CaisseTab/>}
        {activeTab === 'securite'   && <SecuriteTab/>}

        {/* Onglets Général / Établissement / Notifications */}
        {activeTab !== 'whitelabel' && activeTab !== 'caisse' && (
          <>
            {Object.entries(groups)
              .filter(([group]) => {
                if (activeTab === 'general')       return group === 'Général';
                if (activeTab === 'etablissement') return group === 'Établissement';
                if (activeTab === 'notifications') return group === 'Notifications';
                return false;
              })
              .map(([group, items]) => (
                <div key={group} className="card mb-4">
                  <div className="card-title" style={{ marginBottom:16 }}>{group}</div>
                  {items
                    // Clés dynamiques par site (gérées par leur propre UI dédiée dans
                    // l'onglet "Caisse" — voir CaisseTab plus haut) : ne jamais les
                    // laisser réapparaître ici en doublon comme simples champs texte
                    // bruts (ex: "Espèces du jour dans la caisse (Test)").
                    .filter(s => !s.key.startsWith('safe_initial_')
                      && !s.key.startsWith('especes_in_caisse_')
                      && !['enabled_bills','enabled_coins',
                           'cash_alert_threshold','safe_alert_threshold',
                           'safe_initial_amount','safe_initial_date'].includes(s.key))
                    .map(s => (
                      <div key={s.key} className="form-group">
                        <label className="form-label">{s.label || s.key}</label>
                        <input
                          type={s.key.includes('pass') || s.key.includes('api_key') ? 'password' : 'text'}
                          className="input"
                          value={values[s.key] || ''}
                          onChange={e => upd(s.key, e.target.value)}
                        />
                      </div>
                    ))}
                </div>
              ))
            }
          </>
        )}
      </div>
    </>
  );
}

export default AdminSettings;
